import {DateTime, Duration, DurationLikeObject} from "luxon";

export const DATE_INPUT_FORMAT = 'yyyy-LL-dd'

/**
 * Get the system timezone
 */
export function systemZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * Check if the given timezone matches the system timezone
 * @param zone
 */
export function isSameTimezone(zone?: string): boolean {
  // Assume true if no zone provided
  if (!zone) return true;

  return systemZone() === zone;
}

/**
 * Localise a time string to the current user timezone
 *
 * @param time
 * @param timezone
 */
export function localisedTime(time: string, timezone: string = 'UTC'): string {
  const [hour, minute] = time.split(':')

  return DateTime
    .local({zone: timezone})
    .set({hour: Number(hour), minute: Number(minute)})
    .toLocal()
    .toFormat('HH:mm')
}

/**
 * Convert a given number of seconds to a human-readable duration, if specified
 *
 * @param {string|undefined} seconds
 */
export function secondsToHuman(seconds?: number | null): string | undefined {
  if (typeof seconds !== 'number') return;

  let duration: DurationLikeObject = {};

  if (seconds < 60) duration = {seconds};
  if (seconds >= 60 && seconds < 3600) duration = {minutes: 0, seconds};
  if (seconds >= 3600) duration = {hours: 0, minutes: 0, seconds};

  return Duration.fromObject(duration).normalize().toHuman({unitDisplay: "narrow"})
}

/**
 * Format a given ISO date/time string or object, if specified
 *
 * @param dateTime
 * @param zone
 * @param showZone
 */
export function formattedDateTime(dateTime: string | DateTime | null | undefined, showZone: boolean = false, zone: string = systemZone(), locale: string = 'local'): string | undefined {
  if (!dateTime) {
    return undefined
  }
  let luxonDateTime: DateTime;

  if (typeof dateTime === 'string') {
    luxonDateTime = DateTime.fromISO(dateTime, {zone});
  } else {
    luxonDateTime = dateTime.setZone(zone);
  }

  const options: Intl.DateTimeFormatOptions = {...DateTime.DATETIME_MED_WITH_SECONDS, timeZoneName: showZone ? 'short' : undefined};

  return `${luxonDateTime.setLocale(locale).toLocaleString(options)}${showZone ? ` (UTC${luxonDateTime.toFormat('Z')})` : ''}`;
}

/**
 * Format a given ISO date/time string or object, if specified
 *
 * @param dateTime
 */
export function formattedDate(dateTime: string | DateTime | null | undefined): string | undefined {
  if (!dateTime) {
    return undefined
  }

  if (typeof dateTime === 'string') {
    dateTime = DateTime.fromISO(dateTime)
  }

  return dateTime.toLocaleString(DateTime.DATE_MED)
}

/**
 * Format a given ISO date/time string or object for an HTML datetime input
 *
 * @param dateTime
 * @param excludeTime
 */
export function formatDateTimeForInput(dateTime: string | DateTime | null | undefined, excludeTime: boolean = false): string | undefined {
  if (!dateTime) {
    return undefined
  }

  if (typeof dateTime === 'string') {
    dateTime = DateTime.fromISO(dateTime)
  }

  return excludeTime ? dateTime.toFormat("yyyy-LL-dd") : dateTime.toFormat("yyyy-LL-dd'T'HH:mm")
}

/**
 * Format a given date/time as an ISO string
 *
 * @param dateTime
 * @param excludeTime
 * @param excludeOffset
 */
export function formatDateTimeAsISO(dateTime: string | DateTime | null | undefined, excludeTime: boolean = false, excludeOffset: boolean = false): string | undefined {
  if (!dateTime) {
    return undefined
  }

  if (typeof dateTime === 'string') {
    dateTime = DateTime.fromISO(dateTime).startOf('minute')
  }

  return excludeTime ?
    dateTime.toISODate() :
    dateTime.toISO({includeOffset: !excludeOffset})
}

/**
 * Ensure that the given date/time representation is a DateTime object, if specified
 *
 * @param dateTime
 */
export function dateTime(dateTime: string | DateTime | null | undefined): DateTime | undefined {
  if (!dateTime) {
    return undefined
  }

  return typeof dateTime !== 'string' ? dateTime : DateTime.fromISO(dateTime)
}

/**
 * Ensure that the given duration representation is a Duration object, if specified
 *
 * @param duration
 */
export function duration(duration: number | string | Duration | null | undefined): Duration | undefined {
  if (typeof duration !== 'number' && typeof duration !== 'string') {
    return duration || undefined
  }

  if (typeof duration === 'string') {
    return Duration.fromISO(duration).normalize()
  }

  const unitValues = {years: 0, months: 0, weeks: 0, days: 0, hours: 0, minutes: 0, seconds: duration}

  return Duration.fromObject(unitValues).normalize()
}


export function formattedTime(dateTime: string | DateTime| null | undefined, showZone: boolean = false, zone: string = systemZone(), locale: string = 'local'): string | undefined {
  if (!dateTime) {
    return undefined
  }
  let luxonDateTime: DateTime;

  if (typeof dateTime === 'string') {
    luxonDateTime = DateTime.fromISO(dateTime, {zone});
  } else {
    luxonDateTime = dateTime.setZone(zone);
  }

  const options: Intl.DateTimeFormatOptions = {...DateTime.TIME_SIMPLE, timeZoneName: showZone ? 'short' : undefined};

  return `${luxonDateTime.setLocale(locale).toLocaleString(options)}${showZone ? ` (UTC${luxonDateTime.toFormat('Z')})` : ''}`;
}

export function formatTimeDifference(
  startMs?: number,
  endMs?: number
): string | null {
  if (!startMs || !endMs) return null;

  const { hours, minutes, seconds } = Duration
    .fromMillis(Math.abs(endMs - startMs))
    .shiftTo("hours", "minutes", "seconds")
    .toObject();

  const parts: string[] = [];

  if (hours) parts.push(`${hours}h`);
  if (minutes) parts.push(`${minutes}m`);
  parts.push(`${seconds ?? 0}s`);

  return parts.join(", ");
}